














































































































































































































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Mixins / Providers
import HasValidation from "@/mixins/HasValidation";
import ModifyProductMixin from "@/components/app/global/contractProducts/modifyProductMixin";
import ProductConfigProvider from "@/components/app/global/baskets/basket/productConfigProvider";

// Treeselect
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

// Types
import { IProductConfig } from "@/models/providers/products";
import { BACKEND_DATE_FORMAT } from "@/data/date";
import { IInvoice } from "@/models/invoices";
import { RefundSources } from "@/data/enums/payments";
import { IContractProduct } from "@/models/contracts";

enum CHARGE_OPTIONS {
  PRORATA = "prorata",
  MANUAL = "manual",
  NIL = "nil"
}

enum REFUND_OPTIONS {
  PRORATA_TO_SOURCE = "prorata_to_source",
  PRORATA_TO_CREDIT = "prorata_to_credit",
  NIL = "nil"
}

enum MODIFY_TYPES {
  EXISTING = "existing",
  NEW = "new"
}

@Component({
  components: {
    Treeselect,
    ProductConfigProvider,
    "product-options": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productOptions.vue"
      ),
    "product-attributes": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productAttributes.vue"
      ),
    "product-terms": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTerms.vue"
      ),
    "change-product-switcher": () =>
      import(
        "@/components/app/global/contractProducts/changeProductSwitcher.vue"
      ),
    "contract-product-info": () =>
      import(
        "@/components/app/global/contractProducts/contractProductInfo.vue"
      ),
    "editable-price": () =>
      import(
        "@/components/app/global/baskets/basket/productView/editablePrice.vue"
      )
  }
})
export default class ModifyProductModal extends Mixins(
  ModifyProductMixin,
  HasValidation
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    productConfigProvider: ProductConfigProvider;
  };

  // Props

  @Prop({ default: MODIFY_TYPES.EXISTING })
  public readonly modifyAction!: MODIFY_TYPES;

  // Data

  public MODIFY_TYPES = MODIFY_TYPES;
  public delay = false;
  public invoicePreview: IInvoice | null = null;
  public isLoading = true;
  public isProcessingDryRun = false;
  public isReloading = false;
  public modifyType = this.modifyAction;
  public nextDueDate = this.oldContractProduct.next_due_date;
  public proRataAmount = 0;
  public proRataAmountDB = 0;
  public proRataAmountFormatted = "";
  public productConfiguration: IProductConfig | null = null;
  public requestError = false;
  public selectedChargeOptionId = this.chargeOptions[0].id;
  public selectedProductId: string | null = "";
  public selectedRefundOptionId = this.refundOptions[0].id;
  public configurationClone: any = {};
  public isCloned = false;

  // Computed

  public get isRefundable() {
    if (!this.invoicePreview) return false;
    return this.invoicePreview.total_amount_converted < 0;
  }

  public get isChargeable() {
    if (!this.invoicePreview) return false;
    return this.invoicePreview.total_amount_converted > 0;
  }

  public getProrataOrEmpty({ negate = false }: { negate: boolean }) {
    return this.proRataAmountDB !== this.proRataAmount
      ? {
          pro_rata_amount: negate ? this.proRataAmount * -1 : this.proRataAmount
        }
      : {};
  }

  public get chargeOptions() {
    return [
      {
        id: CHARGE_OPTIONS.PRORATA,
        label: this.$t("_sentence.cprod.on_change_apply_prorata_charge", {
          amount: this.proRataAmountFormatted.replace(/\.00/, "")
        }),
        payload: this.getProrataOrEmpty({ negate: false })
      },
      {
        id: CHARGE_OPTIONS.NIL,
        label: this.$t("_sentence.cprod.on_change_apply_zero_charge"),
        amount: undefined,
        payload: {
          pro_rata_amount: 0
        }
      }
    ];
  }

  public get refundOptions() {
    return [
      {
        id: REFUND_OPTIONS.PRORATA_TO_SOURCE,
        label: this.$t("_sentence.cprod.on_change_issue_refund_to_source", {
          amount: this.proRataAmountFormatted.replace(/(\.00)|(-)/, "")
        }),
        payload: {
          ...this.getProrataOrEmpty({ negate: true }),
          refund_source: RefundSources.ORIGINAL_SOURCE
        }
      },
      {
        id: REFUND_OPTIONS.PRORATA_TO_CREDIT,
        label: this.$t("_sentence.cprod.on_change_issue_refund_to_credit", {
          amount: this.proRataAmountFormatted.replace(/(\.00)|(-)/, "")
        }),
        payload: {
          ...this.getProrataOrEmpty({ negate: true }),
          refund_source: RefundSources.WALLET_ONLY
        }
      },
      {
        id: REFUND_OPTIONS.NIL,
        label: this.$t("_sentence.cprod.on_change_issue_no_refund"),
        payload: {
          pro_rata_amount: 0
        }
      }
    ];
  }

  public get selectedRefundOption() {
    return _.find(this.refundOptions, ["id", this.selectedRefundOptionId]);
  }

  public get selectedChargeOption() {
    return _.find(this.chargeOptions, ["id", this.selectedChargeOptionId]);
  }

  public get treeOptions() {
    return _.toArray(
      _.reduce(
        this.products,
        (acc, product) => {
          const group = acc[product.category_id] || {};

          group["id"] = product.category_id;
          group["label"] = product.category.name;

          if (!group["children"]) group["children"] = [];
          group["children"].push({
            id: product.id,
            label: product.name
          });

          acc[product.category_id] = group;
          return acc;
        },
        {}
      )
    );
  }

  public get newProduct() {
    return _.find(this.products, ["id", this.selectedProductId]);
  }

  public get showProvisioningMessage() {
    if (!this.selectedProductId) return false;
    const oldProvisonBlueprintId = _.get(
      this.oldContractProduct,
      "product.provision_blueprint_id",
      null
    );
    const newProvisonBlueprintId = _.get(
      this.productConfiguration,
      "product.provision_blueprint_id",
      null
    );
    if (oldProvisonBlueprintId === newProvisonBlueprintId) return false;
    if (oldProvisonBlueprintId && !newProvisonBlueprintId) return false;
    return true;
  }

  public get hasChange() {
    return !this.$_.isEqual(this.configuration, this.configurationClone);
  }

  public get isValidForm() {
    return (
      !this.delay &&
      this.products.length &&
      this.configuration.product.product_id &&
      this.configuration.product.quantity &&
      this.configuration.product.billing_cycle_months
    );
  }

  public get configuration() {
    return {
      contract_id: this.contract.id,
      contracts_product_id: this.oldContractProduct.id,
      product: {
        product_id: this.selectedProductId,
        billing_cycle_months: _.get(
          this.productConfiguration,
          "billing_cycle_months"
        ),
        /**
         * Incude next due date only if it's changed
         */
        ...(this.nextDueDate !== this.oldContractProduct.next_due_date
          ? { next_due_date: this.nextDueDate }
          : {}),
        quantity: this.oldContractProduct?.quantity || 1,
        price: this.computeConfigPrice(
          this.productConfiguration?.selling_price,
          this.oldContractProduct?.selling_price,
          this.productConfiguration?.total
        )
      },
      options: _.map(
        _.get(this.productConfiguration, "options", []),
        option => {
          const oldOption = _.find(this.oldContractProduct?.options, [
            "product_id",
            option.product_id
          ]);
          return {
            product_id: option.product_id,
            billing_cycle_months: option.billing_cycle_months,
            unit_quantity: option.unit_quantity,
            price: this.computeConfigPrice(
              option?.selling_price,
              oldOption?.selling_price,
              option?.unit_total
            )
          };
        }
      ),
      attributes: _.map(
        _.get(this.productConfiguration, "attributes", []),
        attr => ({
          product_id: attr.product_id
        })
      )
    };
  }

  public computeConfigPrice(
    customPrice: number | undefined,
    contractPrice?: IContractProduct["selling_price"],
    totalPrice?: number
  ) {
    let computedPrice: number | undefined = undefined;
    // 1st: Favour any custom price (if specified)
    if (_.isNumber(customPrice)) computedPrice = customPrice;
    // 2nd: If config total differs from existing price, use new total
    else if (contractPrice !== totalPrice) computedPrice = totalPrice;
    // Return new price if different to existing contract price
    return computedPrice === contractPrice ? undefined : computedPrice;
  }

  // Life-cycle methods

  public async created() {
    this.isLoading = true;
    await this.loadData();
    this.resetForm();
    this.setModifyActionSettings();
  }

  // Methods

  public productConfigProviderLoaded() {
    this.$nextTick(() => {
      if (this.isCloned) return;
      this.configurationClone = this.$_.cloneDeep(this.configuration);
      this.isCloned = true;
    });
  }

  public setModifyActionSettings() {
    if (this.modifyType === MODIFY_TYPES.EXISTING) {
      this.selectedProductId = this.oldContractProduct.product_id;
    }
  }

  public resetForm() {
    this.proRataAmount = 0;
    this.proRataAmountDB = 0;
    this.proRataAmountFormatted = "";
    this.selectedProductId = null;
    this.selectedChargeOptionId = this.chargeOptions[0].id;
    this.selectedRefundOptionId = this.refundOptions[0].id;
  }

  public async loadData() {
    try {
      await this.getProducts(this.isReloading);
      await this.getBrandSettings(this.isReloading);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public abort(error) {
    this.$store.dispatch("api/handleError", error);
    this.selectedProductId = null;
  }

  public selectNextDueDate() {
    const nextDueMoment = this.$moment(this.nextDueDate);
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/date/selectDateModal.vue"),
        parent: this,
        props: {
          title: this.$t("_action.change_next_renewal_date"),
          defaultDate: nextDueMoment.toDate(),
          minDate: this.$moment()
            .startOf("day")
            .toDate(),
          events: [
            this.$moment(this.oldContractProduct.next_due_date).toDate()
          ],
          onSelect: async date => {
            this.nextDueDate = this.$moment(date).format(BACKEND_DATE_FORMAT);
          }
        },
        width: 440
      }
    });
  }

  public async doDryRun() {
    try {
      this.isProcessingDryRun = true;
      this.invoicePreview = await this.changeProduct({
        ...this.configuration,
        dry_run: true
      });
      this.proRataAmount = Math.abs(this.invoicePreview.total_amount_converted);
      this.proRataAmountDB = this.proRataAmount;
      this.proRataAmountFormatted = this.invoicePreview.total_amount_formatted.replace(
        /^-/,
        ""
      );
    } catch {
      // Silently fail
    } finally {
      this.isProcessingDryRun = false;
    }
  }

  public async setProRataAmount(amount) {
    this.proRataAmount = amount;
    this.proRataAmountFormatted = await this.$store.dispatch("api/calculate", {
      prices: [amount],
      currency_id: this.contract.currency_id,
      returnOnly: "total_formatted"
    });
  }

  public async validateBeforeSubmit() {
    if (!(await this.validate())) return;
    try {
      this.isProcessing = true;
      const invoice = await this.changeProduct({
        ...this.configuration,
        ...(this.isRefundable ? this.selectedRefundOption?.payload : {}),
        ...(this.isChargeable ? this.selectedChargeOption?.payload : {})
      });
      // Route to invoice if unpaid amount
      if (invoice.unpaid_amount_converted > 0) {
        this.$router
          .push({
            name: `adminInvoice`,
            params: {
              clientId: invoice.client_id,
              invoiceId: invoice.id
            }
          })
          .catch((this.$router as any).handleFailure);
      }
      // Show success toast
      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.contract")
        })
      });
      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.isProcessing = false;
    }
  }

  // Watchers

  @Watch("configuration", { deep: true })
  public onConfigurationChange(newVal, oldVal) {
    if (_.isEqual(newVal, oldVal)) return;
    this.invoicePreview = null;
    this.$nextTick(async () => {
      const hasValidObserver = await this.validate(true);
      if (!this.isValidForm || !hasValidObserver) return;
      return this.doDryRun();
    });
  }

  @Watch("modifyType")
  public onModifyTypeChange(value) {
    if (value === MODIFY_TYPES.EXISTING) {
      this.setModifyActionSettings();
      return;
    }
    this.resetForm();
  }

  @Watch("selectedProductId")
  public onSelectedProductChange(value) {
    if (!value) return;
    this.$nextTick(async () => {
      this.delay = true;
      await Promise.all([
        // Add small delay for smoother UX transition
        new Promise(resolve => setTimeout(resolve, 0))
      ]);
      this.delay = false;
    });
  }
}
